// API client for QuizSecure backend

import type { 
  APIResponse, 
  AuthResponse, 
  ExamQuestion, 
  ExamSubmissionResponse,
  CalibrationInfo,
  StudentSessionInfo
} from '@/types';

interface ExamQuestionsWireResponse {
  success: boolean;
  questions: ExamQuestion[];
  sessionInfo: {
    studentName: string;
    startTime: string;
  };
  message?: string;
}

interface ExamSubmissionWireResponse {
  success: boolean;
  score: number;
  correct_answers: number;
  total_questions: number;
  message: string;
  redirect_url: string;
}

interface StudentSessionsWireResponse {
  success: boolean;
  sessions: StudentSessionInfo[];
}

class APIClient {
  private baseUrl: string;

  constructor(baseUrl: string = '') {
    this.baseUrl = baseUrl;
  }

  private async request<T>(
    endpoint: string, 
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    
    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      
      // Handle array of errors (common in 422 responses)
      if (Array.isArray(errorData.detail)) {
        const errorMessages = errorData.detail.map((err: any) => 
          typeof err === 'object' ? err.msg || err.message || JSON.stringify(err) : String(err)
        );
        throw new Error(errorMessages.join(', '));
      }
      
      throw new Error(errorData.detail || errorData.message || `HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  }

  private async formRequest<T>(
    endpoint: string,
    formData: FormData
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    
    let response: Response;
    try {
      response = await fetch(url, {
        method: 'POST',
        body: formData,
      });
    } catch (fetchErr) {
      throw fetchErr;
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      
      // Handle array of errors (common in 422 responses)
      if (Array.isArray(errorData.detail)) {
        const errorMessages = errorData.detail.map((err: any) => 
          typeof err === 'object' ? err.msg || err.message || JSON.stringify(err) : String(err)
        );
        throw new Error(errorMessages.join(', '));
      }
      
      throw new Error(errorData.detail || errorData.message || `HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  }

  // Authentication endpoints
  async authenticate(name: string, role: 'student' | 'teacher'): Promise<AuthResponse> {
    const formData = new FormData();
    formData.append('name', name);
    formData.append('role', role);
    
    return this.formRequest<AuthResponse>('/api/auth', formData);
  }

  async submitConsent(
    sessionId: string,
    gazeTracking: boolean = true,
    behavioralAnalysis: boolean = true,
    integrityMonitoring: boolean = true,
    cameraRecording: boolean = true
  ): Promise<APIResponse> {
    const formData = new FormData();
    formData.append('session_id', sessionId);
    formData.append('gaze_tracking', gazeTracking.toString());
    formData.append('behavioral_analysis', behavioralAnalysis.toString());
    formData.append('integrity_monitoring', integrityMonitoring.toString());
    formData.append('camera_recording', cameraRecording.toString());
    
    return this.formRequest<APIResponse>('/api/consent', formData);
  }

  async giveConsent(sessionId: string, consentData: Record<string, boolean>): Promise<APIResponse<void>> {
    return this.request<APIResponse<void>>('/api/consent', {
      method: 'POST',
      body: JSON.stringify({ 
        session_id: sessionId,
        consent_data: consentData 
      }),
    });
  }

  async logout(sessionId: string): Promise<APIResponse> {
    const formData = new FormData();
    formData.append('session_id', sessionId);
    
    return this.formRequest<APIResponse>('/api/logout', formData);
  }

  // Exam endpoints
  async getExamQuestions(sessionId: string): Promise<APIResponse<{
    questions: ExamQuestion[];
    sessionInfo: {
      studentName: string;
      startTime: string;
    };
  }>> {
    const response = await this.request<ExamQuestionsWireResponse>(
      `/api/exam-questions?session_id=${sessionId}`
    );

    return {
      success: response.success,
      data: {
        questions: response.questions,
        sessionInfo: response.sessionInfo,
      },
      message: response.message,
    };
  }

  async submitExam(sessionId: string, answers: Record<number, number>): Promise<ExamSubmissionResponse> {
    const formData = new FormData();
    formData.append('session_id', sessionId);
    formData.append('answers', JSON.stringify(answers));
    
    const response = await this.formRequest<ExamSubmissionWireResponse>('/api/submit-exam', formData);

    return {
      success: response.success,
      score: response.score,
      correctAnswers: response.correct_answers,
      totalQuestions: response.total_questions,
      message: response.message,
      redirectUrl: response.redirect_url,
    };
  }

  // Calibration endpoints
  async getCalibrationStatus(sessionId: string): Promise<CalibrationInfo> {
    return this.request<CalibrationInfo>(`/api/calibration-status?session_id=${sessionId}`);
  }

  /** Client-side WebGazer flags for instructor visibility */
  async postMonitoringEvent(payload: {
    session_id: string;
    alert_level: string;
    focus_percentage: number;
    flags: string[];
    behavioral_flags?: Record<string, boolean>;
    gaze_x?: number | null;
    gaze_y?: number | null;
    timestamp?: string;
  }): Promise<{ status: string }> {
    return this.request('/api/monitoring-events', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  // Session management
  async validateSession(sessionId: string): Promise<APIResponse<{
    valid: boolean;
    reason?: string;
    sessionData?: any;
  }>> {
    return this.request<APIResponse<{
      valid: boolean;
      reason?: string;
      sessionData?: any;
    }>>(`/api/validate-session?session_id=${sessionId}`);
  }

  async resetSession(sessionId: string): Promise<APIResponse> {
    const formData = new FormData();
    formData.append('session_id', sessionId);
    
    return this.formRequest<APIResponse>('/api/reset-session', formData);
  }

  // Instructor endpoints
  async getStudentSessions(): Promise<APIResponse<StudentSessionInfo[]>> {
    const response = await this.request<StudentSessionsWireResponse>('/api/student-sessions');
    return {
      success: response.success,
      data: response.sessions,
    };
  }

  async exportSessionData(sessionId: string): Promise<APIResponse> {
    return this.request<APIResponse>(`/api/export-session-data?session_id=${sessionId}`);
  }

  async cleanupSessions(): Promise<APIResponse> {
    return this.request<APIResponse>('/api/cleanup-sessions', { method: 'POST' });
  }

  // Health check
  async healthCheck(): Promise<APIResponse> {
    return this.request<APIResponse>('/api/health');
  }
}

// WebSocket manager for real-time communication
export class WebSocketManager {
  private ws: WebSocket | null = null;
  private sessionId: string = '';
  private listeners: Map<string, Set<(data: any) => void>> = new Map();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private heartbeatInterval: NodeJS.Timeout | null = null;
  private connectionPromise: Promise<void> | null = null;
  private isIntentionalDisconnect = false;

  connect(sessionId: string): Promise<void> {
    // If already connecting to the same session, return existing promise
    if (this.connectionPromise && this.sessionId === sessionId) {
      return this.connectionPromise;
    }

    // If already connected to the same session, resolve immediately
    if (this.ws && this.ws.readyState === WebSocket.OPEN && this.sessionId === sessionId) {
      console.log('🔗 WebSocket already connected for session:', sessionId);
      return Promise.resolve();
    }

    // Create new connection promise
    this.connectionPromise = this._createConnection(sessionId);
    return this.connectionPromise;
  }

  private _createConnection(sessionId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        // Clean up existing connection
        this._cleanup();

        this.sessionId = sessionId;
        this.isIntentionalDisconnect = false;
        
        // Determine WebSocket URL based on environment
        const isDev = (import.meta as any).env?.DEV || window.location.hostname === 'localhost';
        const wsUrl = isDev 
          ? `ws://localhost:8000/ws/${sessionId}`
          : `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/ws/${sessionId}`;
        
        console.log('🔗 Connecting to WebSocket:', wsUrl);
        this.ws = new WebSocket(wsUrl);
        
        // Add connection timeout
        const connectionTimeout = setTimeout(() => {
          if (this.ws && this.ws.readyState === WebSocket.CONNECTING) {
            console.error('❌ WebSocket connection timeout');
            this.ws.close();
            reject(new Error('WebSocket connection timeout'));
          }
        }, 10000); // 10 second timeout

        this.ws.onopen = () => {
          clearTimeout(connectionTimeout);
          console.log('✅ WebSocket connected successfully');
          this.reconnectAttempts = 0;
          this.connectionPromise = null;
          
          // Start heartbeat
          this._startHeartbeat();
          
          // Emit 'open' event to notify listeners
          this._emitEvent('open', {});
          
          resolve();
        };
      
        this.ws.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data);
            
            // Handle heartbeat responses
            if (message.type === 'pong') {
              console.log('💓 Heartbeat response received');
              return;
            }
            
            // Log monitoring data at debug level
            if (message.type === 'monitoring_result') {
              console.debug('📊 Monitoring data received:', message.data?.system_used);
            }
            
            // Use setTimeout to prevent blocking
            setTimeout(() => {
              this._handleMessage(message);
            }, 0);
          } catch (error) {
            console.error('❌ Error parsing WebSocket message:', error);
          }
        };
        
        this.ws.onclose = (event) => {
          clearTimeout(connectionTimeout);
          this._stopHeartbeat();
          
          console.log(`📴 WebSocket disconnected: ${event.code} ${event.reason || ''}`);
          
          // Emit 'close' event
          this._emitEvent('close', { code: event.code, reason: event.reason });
          
          // Only attempt reconnection if not intentional and still have attempts left
          if (!this.isIntentionalDisconnect && this.reconnectAttempts < this.maxReconnectAttempts) {
            this._attemptReconnect();
          } else if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            console.error('❌ Max reconnection attempts reached');
            this._emitEvent('connection_lost', {});
            reject(new Error('Max reconnection attempts reached'));
          }
        };
        
        this.ws.onerror = (error) => {
          clearTimeout(connectionTimeout);
          console.error('❌ WebSocket error:', error);
          
          // Emit 'error' event
          this._emitEvent('error', error);
          
          // Don't reject here immediately, let onclose handle reconnection
        };
        
      } catch (error) {
        console.error('❌ WebSocket connection setup failed:', error);
        reject(error);
      }
    });
  }

  private _startHeartbeat(): void {
    this._stopHeartbeat();
    
    this.heartbeatInterval = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        console.debug('💓 Sending heartbeat');
        this.ws.send(JSON.stringify({ type: 'ping', timestamp: Date.now() }));
      }
    }, 30000); // Send heartbeat every 30 seconds
  }

  private _stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  private _cleanup(): void {
    this._stopHeartbeat();
    
    if (this.ws) {
      this.isIntentionalDisconnect = true;
      this.ws.close();
      this.ws = null;
    }
    
    this.connectionPromise = null;
  }

  disconnect(): void {
    console.log('🔌 Intentionally disconnecting WebSocket');
    this.isIntentionalDisconnect = true;
    this._cleanup();
    this.listeners.clear();
    this.reconnectAttempts = 0;
  }

  send(data: any): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      try {
        this.ws.send(JSON.stringify(data));
      } catch (error) {
        console.error('❌ Error sending WebSocket message:', error);
      }
    } else {
      console.warn('⚠️ WebSocket is not connected, message not sent:', data.type);
    }
  }

  on(event: string, callback: (data: any) => void): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(callback);
  }

  off(event: string, callback: (data: any) => void): void {
    const eventListeners = this.listeners.get(event);
    if (eventListeners) {
      eventListeners.delete(callback);
    }
  }

  private _emitEvent(event: string, data: any): void {
    const eventListeners = this.listeners.get(event);
    if (eventListeners) {
      eventListeners.forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`❌ Error in WebSocket ${event} callback:`, error);
        }
      });
    }
  }

  private _handleMessage(message: any): void {
    try {
      const { type, data } = message;
      
      // Emit specific event type
      this._emitEvent(type, data);
      
      // Also emit generic 'message' event
      this._emitEvent('message', message);
    } catch (error) {
      console.error('❌ Error handling WebSocket message:', error);
    }
  }

  private _attemptReconnect(): void {
    if (this.isIntentionalDisconnect) {
      console.log('🚫 Skipping reconnection - intentional disconnect');
      return;
    }

    this.reconnectAttempts++;
    const delay = Math.min(this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1), 30000);
    
    console.log(`🔄 Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts}) in ${delay}ms...`);
    
    setTimeout(() => {
      if (!this.isIntentionalDisconnect && this.sessionId) {
        this.connect(this.sessionId).catch(error => {
          console.error('❌ Reconnection failed:', error);
        });
      }
    }, delay);
  }

  get isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  get connectionState(): string {
    if (!this.ws) return 'disconnected';
    
    switch (this.ws.readyState) {
      case WebSocket.CONNECTING: return 'connecting';
      case WebSocket.OPEN: return 'connected';
      case WebSocket.CLOSING: return 'closing';
      case WebSocket.CLOSED: return 'disconnected';
      default: return 'unknown';
    }
  }
}

// Export singleton instances
export const apiClient = new APIClient();
export const wsManager = new WebSocketManager();